"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { FiMenu, FiX } from "react-icons/fi";
import Button from "@/components/ui/Button";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("#home");

  useEffect(() => {
    const handleScroll = () => {
      // Hero section height is 720px, so we check if we've scrolled past it
      setIsScrolled(window.scrollY > 100);

      // Detect which section is currently in view
      const sections = ["home", "faq", "contact"];
      const scrollPosition = window.scrollY + window.innerHeight / 2; // Use middle of viewport

      let currentSection = "#home";

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            currentSection = `#${section}`;
            break;
          }
        }
      }

      // Check if we're near the bottom of the page (contact section)
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100) {
        currentSection = "#contact";
      }

      setActiveSection(currentSection);
    };

    handleScroll(); // Call once on mount
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Inicio", href: "#home" },
    { label: "FAQ's", href: "#faq" },
    { label: "Contáctanos", href: "#contact" }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : ''}`}>
      <nav
        className={`border-b transition-colors px-5 md:px-12 lg:px-[100px] py-2.5 flex lg:grid items-center justify-between ${isScrolled ? 'border-gray-200' : 'border-white'}`}
        style={{
          gridTemplateColumns: '1fr auto 1fr'
        }}
      >
          {/* Logo */}
          <div className="flex items-center justify-start">
            <Link href="/" className="flex items-center">
              <div className="relative w-[250px] h-[40px] md:w-[300px] md:h-[50px] lg:w-[509px] lg:h-[80px]">
                <Image
                  src={isScrolled ? "/images/Logo-Full color_text-dark.svg" : "/images/Logo-Full color_text-light.svg"}
                  alt="BloomWise"
                  fill
                  className="object-contain object-left"
                  priority
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Centered (hidden on tablet) */}
          <div className="hidden lg:flex items-center justify-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`transition-colors ${item.href === activeSection ? 'font-bold' : 'font-medium'} ${isScrolled ? 'text-gray-700 hover:text-primary' : 'text-white hover:text-gray-200'}`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA Buttons (hidden on tablet) */}
          <div className="hidden lg:flex items-center justify-end" style={{ gap: '10px' }}>
            {/* <button
              className={`px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 border-2 ${
                isScrolled
                  ? 'border-primary text-primary hover:bg-primary hover:text-white'
                  : 'border-white text-white hover:bg-white hover:text-primary'
              }`}
            >
              Comenzar
            </button> */}
            <a
              href="https://app.bloomwise.fenixoft.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className={`px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 border-2 ${
                isScrolled
                  ? 'border-accent bg-accent text-white hover:bg-accent-hover'
                  : 'border-white bg-accent text-white hover:bg-accent-hover'
              } shadow-md hover:shadow-lg`}
            >
              Iniciar sesión
            </a>
          </div>

          {/* Hamburger Menu Button (visible on mobile & tablet) */}
          <div className="flex lg:hidden justify-end">
            <button
              className={`transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile & Tablet Menu - Right Side Panel */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 lg:hidden z-40 top-[68px]"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Side Panel */}
            <div className={`fixed top-[68px] right-0 w-[55%] max-w-sm lg:hidden z-50 transform transition-transform duration-300 ${isScrolled ? 'bg-primary' : 'bg-white'} shadow-xl rounded-bl-lg`}>
              <div className="flex flex-col px-8 py-8">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`py-3 transition-colors text-lg ${item.href === activeSection ? 'font-bold' : 'font-medium'} ${isScrolled ? 'text-white hover:text-gray-200' : 'text-gray-700 hover:text-primary'}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="flex flex-col space-y-4 mt-12">
                  {/* <button
                    className={`w-full px-6 py-3 text-base font-medium rounded-full transition-all duration-200 border-2 ${
                      isScrolled
                        ? 'border-white text-white hover:bg-white hover:text-primary'
                        : 'border-primary text-primary hover:bg-primary hover:text-white'
                    }`}
                  >
                    Start
                  </button> */}
                  <a
                    href="https://app.bloomwise.fenixoft.ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-6 py-3 text-base font-medium rounded-full text-center transition-all duration-200 bg-accent text-white hover:bg-accent-hover shadow-md hover:shadow-lg"
                  >
                    Login
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
    </header>
  );
}
